import {
  type ComponentType,
  createElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { isEqual } from 'lodash';
import { Box, Link, Typography } from '@mui/material';
import { Dayjs } from 'dayjs';
import PQueue from 'p-queue';
import { FormProvider, useForm } from 'react-hook-form';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import debounce from 'lodash/debounce';
import { toast } from 'sonner';
import type { UseQueryResult } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import {
  NewCampaignStore,
  CampaignFormModel,
  CampaignFormSchema,
  FileErrorType,
  Status,
} from '../../../models/campaign';
import type { Promotion } from '../../../models/promotion';
import { ZonePlacement } from '../../../models/zoneplacement';
import {
  convertToZonePlacements,
  extractZonePlacementIdsForSubmission,
  prepareImageForUpload,
} from '../../../utils';
import { http } from '@/utils/http';
import ErrorBanner from '../../banners/ErrorBanner';
import WarningBanner from '../../banners/WarningBanner';
import PromotionCreative from '../../promotions/PromotionCreative';
import PromotionForm from '../../promotions/PromotionForm';
import ConfirmationModal from '../../promotions/ConfirmationModal';
import {
  AssetAspectError,
  AssetAspectWithStoreError,
  AssetResolutionError,
  AssetTypeError,
  MediaReadError,
  VideoDurationError,
  VideoSizeError,
} from '../../promotions/ErrorMessage';
import ZonePlacementSelector from '../../promotions/ZonePlacementSelector';
import TargetSelector from '../../promotions/TargetSelector';
import Loader from '../../Loader';
import NavBar from '../../navbar';
import EditPromotionNavBar from '../../EditPromotionNavBar';
import { ERROR, NO_END_DATE_TIMESTAMP, SUPPORT_EMAIL } from '@/utils/constants';
import { useUser } from '@/components/auth-provider';
import PageTitle from './page-title';
import { useBoostMultipliers, usePromotion } from './hooks';
import {
  useBanners,
  usePlacements,
  useProperties,
} from '../PromotionList/hooks';
import { djs } from '@/lib/formatters';

interface FileErrorProps {
  name: string;
  reason?: string;
}

interface FileError {
  type: FileErrorType;
  fileName: string;
  reason?: string;
}

type UploadResponseState = {
  id: number;
  duration: number;
} | null;

const errorComponentMap: Record<
  FileErrorType,
  ComponentType<FileErrorProps>
> = {
  'unsupported-file-type': AssetTypeError,
  'invalid-file-size': VideoSizeError,
  'invalid-video-duration': VideoDurationError,
  'invalid-asset-aspect-ratio': AssetAspectError,
  'invalid-asset-aspect-ratio-for-store': AssetAspectWithStoreError,
  'invalid-asset-resolution': AssetResolutionError,
  'unreadable-media': MediaReadError,
};

interface Props {
  campaignId?: number;
  campaignStatus?: Status;
  refetch: UseQueryResult['refetch'];
}

interface Params {
  'zone_ids[]'?: number[];
  'property_values[]'?: number[];
  'placement_ids[]'?: number[];
  'banner_ids[]'?: number[];
  'states[]'?: string[];
}

interface InitialFormValues {
  name: string;
  startDate?: Dayjs | null;
  endDate?: Dayjs | null;
  selectedZones: ZonePlacement[];
  activeStores: number[];
  isNoEndDate: boolean;
  selectedPropertyValues: number[];
  selectedZonePlacements: { zoneId: number; placementIds: number[] }[];
  selectedBanners: number[];
  selectedStates: string[];
  fileUrl?: string;
  assetId?: string | number | null;
  boostMultiplier: number[];
}

interface UploadResponse {
  upload_key: string;
  upload_url: string;
}

const uploadFileQueue = new PQueue({ concurrency: 1 });
const PUBLISH_ERROR =
  'There was an issue publishing this promotion, please try again';

export default function EditPromotion({
  campaignId,
  campaignStatus,
  refetch,
}: Props) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const [openDiscardModal, setOpenDiscardModal] = useState(false);
  const [fileError, setFileError] = useState<FileError | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingStores, setIsLoadingStores] = useState(false);
  const [isBannerExpanded, setIsBannerExpanded] = useState(false);
  const [isStateExpanded, setIsStateExpanded] = useState(false);
  const [stores, setStores] = useState<NewCampaignStore[]>();
  const [targetingSubmissionError, setTargetingSubmissionError] =
    useState(false);
  const [storeLoadingError, setStoreLoadingError] = useState<Error | null>(
    null,
  );
  const [failedToUploadAsset, setFailedToUploadAsset] = useState(false);
  const [pendingUploads, setPendingUploads] = useState(false);
  const fileUploadResponse = useRef<UploadResponseState>(null);
  const assetSelectionGeneration = useRef(0);
  const storesFetchGeneration = useRef(0);
  const isSubmittingRef = useRef(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEndNowModal, setShowEndNowModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(Boolean(campaignId));
  const initializedCampaignId = useRef<number | undefined>(undefined);
  const form = useForm({
    resolver: yupResolver(CampaignFormSchema),
    mode: 'all',
    defaultValues: {
      name: '',
      startDate: null,
      endDate: null,
      selectedZones: [],
      storeSearchTerm: '',
      activeStores: [],
      isNoEndDate: false,
      file: {},
      selectedPropertyValues: [],
      durationList: [],
      selectedZonePlacements: [],
      selectedBanners: [],
      selectedStates: [],
      boostMultiplier: [1],
    },
  });
  const { formState, reset, setError, setValue, watch } = form;
  const selectedZones = watch('selectedZones') ?? [];
  const selectedPropertyValues = watch('selectedPropertyValues');
  const selectedZonePlacements = watch('selectedZonePlacements') ?? [];
  const selectedBanners = watch('selectedBanners') ?? [];
  const selectedStates = watch('selectedStates') ?? [];
  const activeStores = watch('activeStores') ?? [];
  const name = watch('name');
  const startDate = watch('startDate');
  const endDate = watch('endDate');
  const isNoEndDate = watch('isNoEndDate');
  const fileUrl = watch('file.assetUrl');
  const fileAssetId = watch('file.assetId');
  const boostMultiplier = watch('boostMultiplier');
  const initialValues = useRef<InitialFormValues>({
    name: '',
    startDate: null,
    endDate: null,
    selectedZones: [],
    activeStores: [],
    isNoEndDate: false,
    selectedPropertyValues: [],
    selectedZonePlacements: [],
    selectedBanners: [],
    selectedStates: [],
    fileUrl: undefined,
    assetId: null,
    boostMultiplier: [1],
  });
  const { user } = useUser();
  const { data: banners, status: bannersStatus } = useBanners();
  const { data: boostMultipliers } = useBoostMultipliers();
  const { data: placements } = usePlacements();
  const { data: promotion, status: promotionStatus } = usePromotion(campaignId);
  const { data: properties, status: propertiesStatus } = useProperties();
  const multipliers = boostMultipliers ?? [];

  function fetchStores(
    zoneIds: number[],
    zonePlacementIds: number[] = [],
    bannerIds: number[] = [],
    states: string[] = [],
    propertyValues: number[] = [],
  ) {
    const params: Params = {
      'zone_ids[]': zoneIds,
      'placement_ids[]': zonePlacementIds,
      'banner_ids[]': bannerIds,
      'states[]': states,
      'property_values[]': propertyValues,
    };

    return http.get('/stores', { params });
  }

  useEffect(() => {
    async function fetchSelectedZones() {
      if (!campaignId || !placements || !promotion) return;

      const banner_ids = promotion.targeting?.filters?.banner_ids ?? [];
      const placement_ids =
        promotion.targeting?.placements.map((p) => p.id) ?? [];
      const property_values =
        promotion.targeting?.filters?.store_property_value_ids ?? [];
      const stores = promotion.targeting?.stores ?? [];
      const states = promotion.targeting?.filters?.states ?? [];
      const zone_ids = promotion.targeting?.zones.map((z) => z.id) ?? [];

      if (
        (!zone_ids || zone_ids.length === 0) &&
        (!placement_ids || placement_ids.length === 0)
      ) {
        setTargetingSubmissionError(true);
      }

      reset({
        ...form.getValues(),
        activeStores: stores.map((store: { id: number }) => store.id),
      });

      const selectedZones = placements.filter((p) => zone_ids.includes(p.id));
      const selectedZonePlacements = convertToZonePlacements(
        placements,
        zone_ids,
        placement_ids,
      );

      if (property_values.length > 0) {
        reset({
          ...form.getValues(),
          selectedPropertyValues: property_values,
        });
      }

      if (banner_ids.length > 0) {
        setIsBannerExpanded(true);
        reset({
          ...form.getValues(),
          selectedBanners: banner_ids,
        });
      }

      if (states.length > 0) {
        setIsStateExpanded(true);
        reset({
          ...form.getValues(),
          selectedStates: states,
        });
      }

      reset({
        ...form.getValues(),
        selectedZones,
        selectedZonePlacements,
      });
      initialValues.current = {
        ...initialValues.current,
        selectedZones,
        activeStores: stores.map((store: { id: number }) => store.id),
        selectedPropertyValues: property_values,
        selectedZonePlacements,
        selectedBanners: banner_ids,
        selectedStates: states,
        fileUrl: form.getValues('file.assetUrl'),
        assetId: form.getValues('file.assetId'),
      };

      await fetchStoresByZoneIds(
        zone_ids,
        property_values,
        placement_ids,
        banner_ids,
        states,
      );
    }

    if (placements && promotion) {
      fetchSelectedZones();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placements, promotion]);

  useEffect(() => {
    if (!campaignId) {
      setIsInitializing(false);
      return;
    }

    if (targetingSubmissionError && promotionStatus === 'error') {
      setIsInitializing(false);
      return;
    }

    // Edit mode - check all required data
    const isDataLoading =
      promotionStatus === 'pending' ||
      bannersStatus === 'pending' ||
      propertiesStatus === 'pending';

    const hasErrors = promotionStatus === 'error' || bannersStatus === 'error';

    const hasRequiredData =
      promotion && (selectedZones?.length || selectedZonePlacements.length);

    if (
      (!isDataLoading &&
        !hasErrors &&
        hasRequiredData &&
        initializedCampaignId.current !== campaignId) ||
      (targetingSubmissionError && promotion)
    ) {
      const currentFile = form.getValues('file') || {};
      const asset = promotion.creative.assets.find(
        (a) => a.type === 'thumbnail',
      );
      const shouldInitializeFile =
        formState.defaultValues?.file?.assetId ===
        form.getValues('file.assetId');
      const boostMultiplier = promotion.boost_multiplier
        ? [promotion.boost_multiplier.multiplier]
        : [1];
      const endDate = djs(
        promotion.duration.ends_at ?? NO_END_DATE_TIMESTAMP,
      ).startOf('day');
      const startDate = djs(promotion.duration.starts_at);

      reset({
        ...form.getValues(),
        name: promotion.name,
        endDate,
        isNoEndDate: promotion.is_evergreen,
        startDate,
        boostMultiplier,
        ...(shouldInitializeFile &&
          asset && {
            file: {
              ...currentFile,
              assetId: asset.id,
              assetType: 'image/png',
              assetUrl: asset.url,
              duration: promotion.creative.asset_duration_ms / 1000,
            },
          }),
      });

      initialValues.current = {
        ...initialValues.current,
        name: promotion.name,
        endDate,
        isNoEndDate: promotion.is_evergreen,
        startDate,
        fileUrl: asset?.url ?? '',
        assetId: asset?.id ?? null,
        boostMultiplier,
      };

      fileUploadResponse.current = {
        id: promotion.creative.id,
        duration: promotion.creative.asset_duration_ms / 1000,
      };

      setIsInitializing(false);

      initializedCampaignId.current = campaignId;
    }
  }, [
    promotion,
    promotionStatus,
    bannersStatus,
    targetingSubmissionError,
    selectedZones?.length,
    selectedZonePlacements.length,
    campaignId,
  ]);

  async function fetchStoresByZoneIds(
    zoneIds: number[],
    selectedPropertyValues: number[] = [],
    zonePlacementIds: number[] = [],
    bannerIds: number[] = [],
    states: string[] = [],
  ) {
    if (zoneIds.length > 0 || zonePlacementIds.length > 0) {
      const generation = ++storesFetchGeneration.current;

      const validPropertyValues: number[] = selectedPropertyValues.filter(
        (value: number | undefined): value is number => value !== undefined,
      );

      try {
        const storesRes = await fetchStores(
          zoneIds,
          zonePlacementIds.length > 0 ? zonePlacementIds : [],
          bannerIds.length > 0 ? bannerIds : [],
          states.length > 0 ? states : [],
          validPropertyValues.length > 0 ? validPropertyValues : [],
        );

        if (storesFetchGeneration.current !== generation) {
          return;
        }

        const newStores = storesRes.data.sort(
          (a: NewCampaignStore, b: NewCampaignStore) =>
            a.name.localeCompare(b.name),
        );

        setStores(newStores);

        const filteredActiveStores = (
          form.getValues('activeStores') as number[]
        ).filter((storeId) =>
          newStores.some((store: { id: number }) => store.id === storeId),
        );

        setValue('activeStores', filteredActiveStores);
      } catch (error) {
        if (storesFetchGeneration.current === generation) {
          setStoreLoadingError(error as Error);
        }
      } finally {
        if (storesFetchGeneration.current === generation) {
          setIsLoadingStores(false);
        }
      }
    }
  }

  const fetchStoresByZoneIdsRef = useRef(fetchStoresByZoneIds);
  fetchStoresByZoneIdsRef.current = fetchStoresByZoneIds;

  const debouncedFetchStores = useMemo(
    () =>
      debounce(
        (...args: Parameters<typeof fetchStoresByZoneIds>) =>
          fetchStoresByZoneIdsRef.current(...args),
        300,
      ),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedFetchStores.cancel();
    };
  }, [debouncedFetchStores]);

  function handleStoreFiltersChange() {
    storesFetchGeneration.current += 1;

    const values = form.getValues();
    const storeAreaIds = (values.selectedZones ?? []).map(
      (zone: ZonePlacement) => zone.id,
    );
    const placementIds = (values.selectedZonePlacements ?? [])
      .flatMap(
        (s: { zoneId: number; placementIds: (number | undefined)[] }) =>
          s.placementIds,
      )
      .filter((id): id is number => id !== undefined);

    if (storeAreaIds.length > 0 || placementIds.length > 0) {
      setIsLoadingStores(true);
      debouncedFetchStores(
        storeAreaIds,
        (values.selectedPropertyValues ?? []).filter(
          (v): v is number => typeof v === 'number',
        ),
        placementIds,
        (values.selectedBanners ?? []).filter(
          (v): v is number => v !== undefined,
        ),
        (values.selectedStates ?? []).filter(
          (v): v is string => v !== undefined,
        ),
      );
    } else {
      debouncedFetchStores.cancel();
      setIsLoadingStores(false);
      setStoreLoadingError(null);
    }
  }

  const currentStartTimestamp = promotion?.duration.starts_at;
  const currentStartDate =
    currentStartTimestamp && campaignStatus !== undefined
      ? djs(currentStartTimestamp)
      : undefined;

  const currentEndTimestamp = promotion?.duration.ends_at;
  const currentEndDate =
    currentEndTimestamp && campaignStatus !== undefined
      ? djs(currentEndTimestamp)
      : undefined;

  const hasCampaignChanged = useMemo(() => {
    if (
      targetingSubmissionError &&
      (selectedZones?.length || selectedZonePlacements.length)
    ) {
      return true;
    }
    if (!initialValues.current) return false;

    const initial = initialValues.current;

    return (
      !isEqual(name?.trim(), initial.name?.trim()) ||
      !isEqual(startDate, initial.startDate) ||
      !isEqual(endDate, initial.endDate) ||
      !isEqual(isNoEndDate, initial.isNoEndDate) ||
      !isEqual(selectedZones, initial.selectedZones) ||
      !isEqual(selectedZonePlacements, initial.selectedZonePlacements) ||
      !isEqual(selectedBanners, initial.selectedBanners) ||
      !isEqual(selectedStates, initial.selectedStates) ||
      !isEqual(selectedPropertyValues, initial.selectedPropertyValues) ||
      !isEqual(activeStores.sort(), initial.activeStores.sort()) ||
      !isEqual(fileUrl, initial.fileUrl) ||
      !isEqual(fileAssetId, initial.assetId) ||
      !isEqual(boostMultiplier?.[0], initial.boostMultiplier[0])
    );
  }, [
    name,
    startDate,
    endDate,
    isNoEndDate,
    selectedZones,
    selectedZonePlacements,
    selectedBanners,
    selectedStates,
    selectedPropertyValues,
    activeStores,
    fileUrl,
    fileAssetId,
    targetingSubmissionError,
    boostMultiplier,
  ]);

  const isSubmitDisabled = useMemo(() => {
    if (isSubmitting || pendingUploads || isLoadingStores) return true;

    const hasValidSelections = Boolean(
      (selectedZones?.length || selectedZonePlacements.length) &&
      activeStores?.length > 0 &&
      fileUploadResponse.current?.id,
    );

    // Edit mode
    if (campaignId) {
      return !hasCampaignChanged || !hasValidSelections;
    }

    // Create mode
    return !hasValidSelections || !formState.isValid;
  }, [
    isSubmitting,
    pendingUploads,
    isLoadingStores,
    campaignId,
    formState.isValid,
    selectedZonePlacements,
    selectedZones,
    hasCampaignChanged,
    activeStores?.length,
  ]);

  useEffect(() => {
    if (pathname === '/promotions/create') {
      const beforeUnloadHandler = (e: BeforeUnloadEvent) => {
        if (hasCampaignChanged) {
          e.preventDefault();
          e.returnValue = '';
        }
      };

      window.addEventListener('beforeunload', beforeUnloadHandler);

      return () => {
        window.removeEventListener('beforeunload', beforeUnloadHandler);
      };
    }
  }, [pathname, hasCampaignChanged]);

  function handleFileError(
    fileErrorType: FileErrorType | 'clear-error',
    fileName: string,
    reason?: string,
  ) {
    if (fileErrorType === 'clear-error') {
      setFileError(null);
      return;
    } else {
      setFileError({ type: fileErrorType, fileName, reason });
    }
  }

  async function handleCampaignSubmission(
    isUpdateOperation: boolean,
    data: CampaignFormModel,
  ) {
    const newCreativeId = fileUploadResponse.current?.id;
    const hasError =
      !user ||
      !newCreativeId ||
      (!selectedZones?.length && !selectedZonePlacements?.length);

    if (hasError) {
      toast.error(PUBLISH_ERROR);
      return;
    }

    const newMultiplier = multipliers.find(
      (m) => m.multiplier === data.boostMultiplier[0],
    );
    const payload = {
      boost_multiplier_id: newMultiplier?.id ?? 1,
      creative_id: newCreativeId,
      description: 'In-store promotion',
      duration: {
        ends_at: data.isNoEndDate ? null : djs(data.endDate).toISOString(),
        starts_at: djs(data.startDate).toISOString(),
      },
      is_evergreen: data.isNoEndDate,
      name: data.name,
      owner_id: user.company.id,
    };
    let promotionData: Promotion | null = null;
    let promotionStatus = 500;

    if (isUpdateOperation && campaignId) {
      const updateResult = await http.put<Promotion>(
        `/promotions/${campaignId}`,
        payload,
      );

      promotionData = updateResult.data;
      promotionStatus = updateResult.status;
    } else {
      const createResult = await http.post<Promotion>('/promotions', payload);

      promotionData = createResult.data;
      promotionStatus = createResult.status;
    }

    if (![200, 201].includes(promotionStatus) || !promotionData) {
      toast.error(PUBLISH_ERROR);
      return;
    }

    const { zone_ids, placement_ids } = extractZonePlacementIdsForSubmission(
      selectedZones,
      selectedZonePlacements,
    );
    const { status: targetStatus } = await http.post(
      `/promotions/${promotionData.id}/target`,
      {
        stores: data.activeStores?.map((id) => ({ id })) ?? [],
        zones: zone_ids.map((id) => ({ id })),
        placements: placement_ids.map((id) => ({ id })),
        targeting_filter: {
          store_property_value_ids:
            selectedPropertyValues?.filter(Boolean) ?? [],
          banner_ids: selectedBanners.filter(Boolean),
          states: selectedStates.filter(Boolean),
        },
      },
    );

    if (targetStatus !== 204) {
      toast.error(PUBLISH_ERROR);
      navigate(`/promotions/${promotionData.id}`);
      return;
    }

    if (isUpdateOperation) {
      toast.success('Promotion updated successfully');
    } else {
      await refetch();
      navigate(`/promotions?${searchParams.toString()}`);
      toast.success('Promotion scheduled successfully');
    }
  }

  async function handleFileUpload(asset: File, duration: number) {
    const generation = ++assetSelectionGeneration.current;

    setPendingUploads(true);
    try {
      setFailedToUploadAsset(false);

      if (asset.type.startsWith('image/')) {
        asset = await prepareImageForUpload(asset);
      }

      const {
        data: { upload_key, upload_url },
      } = await http.post<UploadResponse>('/promotions/creatives/upload_key', {
        file_name: asset.name,
        file_type: asset.type,
      });

      if (!upload_key || !upload_url) {
        throw new Error('Failed to get upload URL');
      }

      await http.put(upload_url, asset, {
        headers: {
          'Content-Type': asset.type,
        },
        withCredentials: false,
      });

      const { data: creative } = await http.post<{ id: number }>(
        '/promotions/creatives',
        {
          duration_ms: Math.round(duration * 1000),
          file_type: asset.type,
          upload_key,
        },
      );

      if (assetSelectionGeneration.current === generation) {
        fileUploadResponse.current = { duration, id: creative.id };
      }

      return true;
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Something went wrong';

      if (assetSelectionGeneration.current === generation) {
        setError('file', {
          type: 'server',
          message,
        });
        setValue('file', {
          assetUrl: '',
          assetType: '',
          duration: 0,
          assetId: null,
        });
        setFailedToUploadAsset(true);
        setIsSubmitting(false);
      }

      return { error: message };
    } finally {
      if (assetSelectionGeneration.current === generation) {
        setPendingUploads(false);
      }
    }
  }

  const setAssetFromLibrary = useCallback(
    (id: number, duration: number) => {
      uploadFileQueue.clear();
      setPendingUploads(false);
      assetSelectionGeneration.current += 1;
      fileUploadResponse.current = { duration, id };
      form.setValue(
        'file',
        {
          assetId: id.toString(),
          duration,
        },
        { shouldValidate: true },
      );
    },
    [form],
  );

  const clearAssetSelection = useCallback(() => {
    uploadFileQueue.clear();
    setPendingUploads(false);
    assetSelectionGeneration.current += 1;
    fileUploadResponse.current = null;
  }, []);

  async function runSubmit() {
    if (isSubmittingRef.current) return;

    isSubmittingRef.current = true;
    setIsSubmitting(true);

    try {
      await uploadFileQueue.onIdle();

      if (failedToUploadAsset) {
        toast.error(ERROR.GENERIC);
        return;
      }

      const values = form.getValues();
      const latestCreativeId = fileUploadResponse.current?.id;

      if (latestCreativeId) {
        values.file = {
          ...values.file,
          assetId: latestCreativeId.toString(),
        };
      }

      await handleCampaignSubmission(!!campaignId, values);
    } catch (error) {
      toast.error(ERROR.GENERIC);
    } finally {
      setIsSubmitting(false);
      isSubmittingRef.current = false;
    }
  }

  const onSubmitClick = () => {
    const isStartingToday = (startDate as Dayjs).isSame(djs(), 'day');
    if (isStartingToday && !campaignId) {
      setShowScheduleModal(true);
    } else {
      runSubmit();
    }
  };

  const onSubmitConfirm = () => {
    setShowScheduleModal(false);
    runSubmit();
  };

  const handleDiscardCampaign = () => {
    setOpenDiscardModal(false);

    if (campaignId) {
      window.location.reload();
    } else {
      navigate(-1);
    }
  };

  const handleCancel = () => {
    if (hasCampaignChanged) {
      setOpenDiscardModal(true);
    } else {
      if (campaignId) {
        setIsReadOnly(true);
      } else {
        navigate(`/?${searchParams}`);
      }
    }
  };

  const campaignStartDate = useMemo(() => {
    if (!promotion) return null;

    return djs(promotion.duration.starts_at).startOf('day');
  }, [promotion]);

  const showEndNow = useMemo(() => {
    if (isInitializing || !campaignId || !promotion || !campaignStartDate) {
      return false;
    }

    const today = djs().startOf('day');

    return (
      campaignStatus !== 'completed' &&
      (campaignStartDate.isSame(today) || campaignStartDate.isBefore(today))
    );
  }, [
    isInitializing,
    campaignId,
    promotion,
    campaignStatus,
    campaignStartDate,
  ]);

  const showDelete = useMemo(() => {
    if (isInitializing || !campaignId || !promotion || !campaignStartDate) {
      return false;
    }

    const today = djs().startOf('day');

    return campaignStatus === 'scheduled' && campaignStartDate.isAfter(today);
  }, [
    isInitializing,
    campaignId,
    promotion,
    campaignStatus,
    campaignStartDate,
  ]);

  async function handleDeleteConfirm() {
    setShowDeleteModal(false);
    setIsLoading(true);

    try {
      const result = await http.delete(`/promotions/${campaignId}`);

      if (result.status !== 204) {
        throw new Error();
      } else {
        navigate(-1);
      }
    } catch (error) {
      toast.error(PUBLISH_ERROR);
    } finally {
      setIsLoading(false);
    }
  }

  async function handleEndNowConfirm() {
    const message = 'Failed to end promotion';

    setShowEndNowModal(false);
    setIsLoading(true);

    try {
      if (!promotion) {
        throw new Error('Promotion data is not available');
      }

      const result = await http.post(`/promotions/${promotion.id}/cancel`);

      if (result.status !== 204) {
        throw new Error(message);
      } else {
        navigate(-1);
      }
    } catch (error) {
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  }

  const loaderTitle = campaignId
    ? 'Publishing changes…'
    : 'Publishing promotion…';

  function onDuplicate() {
    navigate('/promotions/create');
    setIsReadOnly(false);
    fileUploadResponse.current = promotion?.creative.id
      ? { id: promotion.creative.id, duration: 10 }
      : null;

    const newValues = {
      name: `${name} (copy)`,
      startDate: null,
      endDate: null,
      isNoEndDate: false,
    };

    reset({
      ...form.getValues(),
      ...newValues,
    });

    initialValues.current = {
      ...initialValues.current,
      ...newValues,
    };
  }

  return (
    <FormProvider {...form}>
      <Loader
        active={isInitializing || isSubmitting || isLoading}
        title={
          isSubmitting ? loaderTitle : isLoading ? 'Submitting…' : undefined
        }
        content={
          isSubmitting || isLoading
            ? 'Please do not close or refresh this page until it finishes'
            : undefined
        }
      />
      <div>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            flexDirection: 'column',
          }}
        >
          <NavBar />
          {storeLoadingError && (
            <ErrorBanner>
              <Typography variant="body2" color="error">
                Something went wrong. Check your internet connection and refresh
                the page. If the problem persists contact{' '}
                <Link
                  href={`mailto:${SUPPORT_EMAIL}`}
                  color="error"
                  underline="none"
                >
                  {SUPPORT_EMAIL}
                </Link>
              </Typography>
            </ErrorBanner>
          )}
          {failedToUploadAsset && (
            <Box>
              <ErrorBanner>
                <Typography variant="body2" color="error">
                  There was an issue uploading asset, please try again. If the
                  problem persists contact{' '}
                  <Link
                    href={`mailto:${SUPPORT_EMAIL}`}
                    color="error"
                    underline="none"
                  >
                    {SUPPORT_EMAIL}
                  </Link>
                </Typography>
              </ErrorBanner>
            </Box>
          )}
          <Box
            sx={{
              width: '100%',
              height: '100%',
              justifyContent: 'space-around',
              flexDirection: 'column',
            }}
            display="flex"
          >
            {campaignId && (
              <Box
                sx={{
                  position: 'sticky',
                  zIndex: 1000,
                  top: '64px',
                  backgroundColor: 'secondary.main',
                  paddingTop: '10px',
                  paddingBottom: '10px',
                  boxShadow: '-2px 10px 5px -6px rgba(0,0,0,0.1)',
                  display:
                    !isInitializing &&
                    !isLoadingStores &&
                    promotion &&
                    targetingSubmissionError
                      ? 'flex'
                      : 'none',
                }}
                flexDirection="column"
                width="100%"
              >
                <Box
                  sx={{
                    padding: '0px 10%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    width: '100%',
                  }}
                >
                  <Box
                    display="flex"
                    flexDirection="row"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <Box
                      sx={{
                        width: '100%',
                      }}
                      display="flex"
                      flexDirection="column"
                    >
                      <Box
                        display="flex"
                        flexDirection="row"
                        height="32px"
                        alignItems="center"
                        justifyContent="center"
                        sx={{ width: '100%' }}
                      >
                        {!isInitializing &&
                          !isLoadingStores &&
                          promotion &&
                          (promotionStatus === 'error' ||
                            targetingSubmissionError) && (
                            <Box>
                              <WarningBanner>
                                <Typography
                                  variant="subtitle1"
                                  sx={{ color: 'hint.dark' }}
                                >
                                  {promotionStatus === 'error'
                                    ? 'Promotion data failed to load. Please fill in all promotion details and save again.'
                                    : 'Sorry, promotion Zone and Store selections failed to save. Please try again.'}
                                </Typography>
                              </WarningBanner>
                            </Box>
                          )}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            )}
            <Box display="flex" justifyContent="center">
              <Box
                sx={{
                  maxWidth: '906px',
                }}
                display="flex"
                gap="24px"
                flexDirection="column"
              >
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xl">
                    <PageTitle
                      campaignId={campaignId}
                      isReadOnly={isReadOnly}
                    />
                  </p>
                  {isReadOnly && (
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={onDuplicate}
                        type="button"
                        variant="outline"
                      >
                        Duplicate Promotion
                      </Button>
                      <Button
                        onClick={() => setIsReadOnly(false)}
                        type="button"
                      >
                        Edit Promotion
                      </Button>
                    </div>
                  )}
                </div>
                <PromotionForm
                  campaignStatus={campaignStatus}
                  currentStartDate={currentStartDate}
                  currentEndDate={currentEndDate}
                  isReadOnly={isReadOnly}
                  multipliers={multipliers}
                />

                <ZonePlacementSelector
                  campaignStatus={campaignStatus ?? 'draft'}
                  zonePlacementList={placements}
                  isReadOnly={isReadOnly}
                  onSelectionChange={handleStoreFiltersChange}
                />

                <TargetSelector
                  campaignStatus={campaignStatus}
                  zonePlacementList={placements}
                  campaignStores={stores}
                  isLoadingStores={isLoadingStores}
                  storePropertyList={properties}
                  campaignBannerList={banners}
                  isBannerExpanded={isBannerExpanded}
                  isStateExpanded={isStateExpanded}
                  isReadOnly={isReadOnly}
                  onFilterChange={handleStoreFiltersChange}
                />

                {fileError && errorComponentMap[fileError.type] && (
                  <ErrorBanner>
                    {createElement(errorComponentMap[fileError.type], {
                      name: fileError.fileName,
                      reason: fileError.reason,
                    })}
                  </ErrorBanner>
                )}
                {failedToUploadAsset && (
                  <ErrorBanner>
                    There was an issue uploading this file, please try again. If
                    the problem persists, please contact support@grocerytv.com
                  </ErrorBanner>
                )}
                <PromotionCreative
                  campaignStatus={campaignStatus}
                  handleFileError={handleFileError}
                  uploadFile={handleFileUpload}
                  uploadFileQueue={uploadFileQueue}
                  campaignZonePlacementList={placements}
                  isReadOnly={isReadOnly}
                  setAssetFromLibrary={setAssetFromLibrary}
                  onAssetRemoved={clearAssetSelection}
                />
              </Box>
            </Box>
          </Box>
          {!isReadOnly && (
            <EditPromotionNavBar
              actionsDisabled={isSubmitting || pendingUploads}
              campaignId={campaignId}
              onUserCancel={handleCancel}
              publishDisabled={isSubmitDisabled}
              onDelete={showDelete ? () => setShowDeleteModal(true) : undefined}
              onEndNow={showEndNow ? () => setShowEndNowModal(true) : undefined}
              onSchedule={onSubmitClick}
            />
          )}
        </Box>
      </div>
      <ConfirmationModal
        open={openDiscardModal}
        title={
          campaignId
            ? 'Changes will be discarded'
            : 'New promotion will be discarded'
        }
        content={
          campaignId
            ? 'Promotion changes have not been published yet. If you cancel it now, it will be discarded.'
            : 'This promotion has not been published yet. If you leave now it will be discarded.'
        }
        cancelButtonText="Continue Editing"
        confirmButtonText={campaignId ? 'Discard Changes' : 'Discard Promotion'}
        confirmButtonVariant="destructive"
        onCancel={() => setOpenDiscardModal(false)}
        onConfirm={handleDiscardCampaign}
      />
      <ConfirmationModal
        open={showDeleteModal}
        title={<p className="text-lg font-semibold">Delete Promotion</p>}
        width="710px"
        gap="4px"
        marginTop="12px"
        content="Your promotion will be deleted, and you cannot undo this action."
        cancelButtonText="Cancel"
        confirmButtonText="Delete"
        confirmButtonVariant="destructive"
        onCancel={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
      />
      <ConfirmationModal
        open={showEndNowModal}
        title={
          <p className="text-lg font-semibold">End Promotion Immediately</p>
        }
        width="710px"
        gap="4px"
        marginTop="12px"
        content="Your promotion will stop playing on all screens, and you cannot undo this action."
        cancelButtonText="Cancel"
        confirmButtonText="End Now"
        confirmButtonVariant="destructive"
        onCancel={() => setShowEndNowModal(false)}
        onConfirm={handleEndNowConfirm}
      />
      <ConfirmationModal
        open={showScheduleModal}
        title={
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: '18px',
                color: 'fileInput.dark',
                fontWeight: 600,
              }}
            >
              Your promotion is scheduled for today
            </Typography>
          </Box>
        }
        content="Your promotion will go live immediately. To schedule for a later time, please select a different date."
        cancelButtonText="Cancel"
        confirmButtonText="Schedule"
        confirmButtonVariant="default"
        width="710px"
        gap="4px"
        marginTop="10px"
        onCancel={() => setShowScheduleModal(false)}
        onConfirm={onSubmitConfirm}
      />
    </FormProvider>
  );
}
